import { useEffect, useRef } from "react";
import styled from "styled-components";
import { Code, Terminal, Globe } from "lucide-react";
import gsap from "gsap";
import developer from "../../assets/svgs/developerblue.svg";
import CTASection from "./CTASection";
import { capitalizeWords } from "../../../../services/utils/processors";

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 8rem auto 0;
  padding: 0 2rem;
  @media (max-width: 768px) {
    margin: 4rem auto 0;
  }
`;


const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const TextContent = styled.div``;

const ImageContainer = styled.div`
  width: 25rem;
  height: 25rem;
  @media (max-width :1080px) {
    display: none;
  }
`;

const DeveloperImage = styled.img`
  width: 100%;
  height: 100%;
  transform-origin: center bottom;
`;

const Greeting = styled.p`
  font-size: 1.125rem;
  color: #60a5fa;
  font-weight: 500;
  @media (max-width:720px) {
    font-size: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: white;
  font-family: "Calsans", sans-serif;
  margin-bottom: 2rem;
  @media (max-width:768px) {
    font-size: 1.5rem;
    
  }
`;

const SubTitle = styled.span`
  font-family: "Calsans", sans-serif;
  display: block;
  font-size: 3.5rem;
  line-height: 3.5rem;
  @media (max-width:768px) {
    font-size: 1.9rem;
    line-height: 2rem;
  }
`;

const Description = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  max-width: 42rem;
  line-height: 1.6;
  white-space: pre-line;
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 42rem;
  }
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1rem;
`;


const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-weight: 500;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
`;



const HeroSection = ({profile}) => {
  const greetingRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descriptionRef = useRef(null);
  const badgesRef = useRef(null);
  const badgeElements = useRef([]);
  const developerImageRef = useRef(null);

  useEffect(() => {
    // Set initial states
    gsap.set(
      [
        greetingRef.current,
        titleRef.current,
        subtitleRef.current,
        descriptionRef.current,
      ],
      {
        y: 100,
        opacity: 0,
      }
    );

    gsap.set(badgeElements.current, {
      scale: 0.8,
      opacity: 0,
    });

    // Set initial state for developer image
    gsap.set(developerImageRef.current, {
      opacity: 0,
      x: 100,
    });

    // Create main timeline
    const tl = gsap.timeline({
      defaults: { ease: "power3.out" },
    });

    // Main content animation
    tl.to(greetingRef.current, {
      duration: 0.6,
      y: 0,
      opacity: 1,
    })
      .to(
        titleRef.current,
        {
          duration: 0.6,
          y: 0,
          opacity: 1,
        },
        "-=0.3"
      )
      .to(
        subtitleRef.current,
        {
          duration: 0.6,
          y: 0,
          opacity: 1,
        },
        "-=0.3"
      )
      .to(
        descriptionRef.current,
        {
          duration: 0.6,
          y: 0,
          opacity: 1,
        },
        "-=0.3"
      )
      .to(
        badgeElements.current,
        {
          scale: 1,
          opacity: 1,
          duration: 0.4,
          stagger: 0.1,
          ease: "back.out(1.7)",
        },
        "-=0.2"
      )
      .to(
        developerImageRef.current,
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        "-=0.4"
      );

    // Add floating animation to developer image
    gsap.to(developerImageRef.current, {
      y: -20,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "power1.inOut",
    });

    // Cleanup
    return () => {
      tl.kill();
    };
  }, []);

  return ( 
    <Container>
      <ContentWrapper>
        <FlexContainer>
          <TextContent>
            <Greeting ref={greetingRef}>Hello <span style={{fontSize:'1.3rem'}}>👋🏼</span>, I'm</Greeting>
            <Title ref={titleRef}>
              {capitalizeWords(profile.username)}
              <span style={{fontFamily:"monospace", fontSize:'2rem', color:'#60a5fa'}}>.</span>
              <SubTitle ref={subtitleRef}>Full-Stack Software Developer</SubTitle>
            </Title>

            <Description ref={descriptionRef}>
              {profile.bio}
            </Description>
          </TextContent>

          <ImageContainer>
            <DeveloperImage
              ref={developerImageRef}
              src={developer}
              alt="dev"
            />
          </ImageContainer>
        </FlexContainer>


        <BadgesContainer ref={badgesRef}>
          {[
            {
              icon: <Code size={20} />,
              text: "Full-Stack Development",
              bg: "rgba(59, 130, 246, 0.1)",
              color: "#60a5fa",
            },
            {
              icon: <Terminal size={20} />,
              text: "Clean Architecture",
              bg: "rgba(16, 185, 129, 0.1)",
              color: "#34d399",
            },
            {
              icon: <Globe size={20} />,
              text: "Scalable Solutions",
              bg: "rgba(147, 51, 234, 0.1)",
              color: "#a855f7",
            },
          ].map((badge, index) => (
            <Badge
              key={index}
              ref={(el) => (badgeElements.current[index] = el)}
              $backgroundColor={badge.bg}
              $textColor={badge.color}
            >
              {badge.icon} {badge.text}
            </Badge>
          ))}
        </BadgesContainer>
          <CTASection links={profile.links}></CTASection>
 
      </ContentWrapper>

    </Container>
  );
};

export default HeroSection;